/**
 * Corpus Management Page
 * CRUD for standards_patterns: list, create, edit, archive, bulk export
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, Button, Badge, Spinner, TextInput, Textarea, Select, Modal, Table, Pagination, ToggleSwitch } from 'flowbite-react';
import { HiPlus, HiPencil, HiArchive, HiDownload, HiSearch, HiRefresh, HiFilter, HiLink } from 'react-icons/hi';
import { useStatus } from '../contexts/StatusContext';
import { standardsApi } from '../api/standards';
import { documentsApi } from '../api/documents';
import type { CorpusStandard, StandardCreateInput, StandardUpdateInput } from '../api/standards';
import type { ObjectRelation } from '../api/documents';

const CATEGORIES = [
  'serverless-saas-aws', 'frontend-development', 'database', 'backend',
  'compliance-security', 'well-architected', 'cost-optimization', 'deployment', 'testing',
  'ip-strategy', 'architecture-decisions', 'go-to-market', 'operations',
  'legal-process', 'finance', 'communication', 'product-strategy', 'investor-relations',
  'real-time-systems', 'multi-agent-orchestration'
];

const MATURITY_LEVELS = ['provisional', 'recommended', 'solidified', 'validated', 'reinforced', 'enforced'];
const CONTENT_TYPES = ['standard', 'invariant', 'guideline', 'anti-pattern'];

const maturityColor = (m: string) => {
  switch (m) {
    case 'enforced': return 'success';
    case 'validated': return 'info';
    case 'reinforced': return 'warning';
    default: return 'gray';
  }
};

const EMPTY_FORM: StandardCreateInput = {
  element: '', title: '', rule: '', category: '',
  domain: 'code', priority: 50, content_type: 'standard', maturity: 'provisional',
  description: '', rationale: '', consequences: '', exceptions: '',
};

const CorpusManagement: React.FC = () => {
  const { user } = useStatus();
  const [standards, setStandards] = useState<CorpusStandard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [maturityFilter, setMaturityFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    debounceRef.current = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  // Server-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 25;

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingStandard, setEditingStandard] = useState<CorpusStandard | null>(null);
  const [formData, setFormData] = useState<StandardCreateInput>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  // Archive confirmation
  const [archiveTarget, setArchiveTarget] = useState<CorpusStandard | null>(null);
  const [archiveReason, setArchiveReason] = useState('');

  // Relations panel
  const [relationsStdId, setRelationsStdId] = useState<string | null>(null);
  const [relations, setRelations] = useState<ObjectRelation[]>([]);
  const [loadingRelations, setLoadingRelations] = useState(false);

  // Add link form
  const [linkTargetId, setLinkTargetId] = useState('');
  const [linkTargetType, setLinkTargetType] = useState('entity');
  const [linkRelationType, setLinkRelationType] = useState('related_to');
  const [isLinking, setIsLinking] = useState(false);

  const handleAddLink = async () => {
    if (!relationsStdId || !linkTargetId) return;
    setIsLinking(true);
    try {
      const resp = await documentsApi.createRelation({
        source_object_id: relationsStdId,
        source_object_type: 'invariant',
        target_object_id: linkTargetId.trim(),
        target_object_type: linkTargetType,
        relation_type: linkRelationType,
      });
      if (resp.success) {
        setLinkTargetId('');
        loadRelations(relationsStdId);
        setSuccessMsg('Relation created');
      } else {
        setError(resp.error || 'Failed to create relation');
      }
    } catch (err) {
      setError('Failed to create relation');
    } finally {
      setIsLinking(false);
    }
  };

  const loadRelations = async (patternId: string) => {
    setRelationsStdId(patternId);
    setLoadingRelations(true);
    try {
      const resp = await documentsApi.getRelations(patternId);
      if (resp.success && resp.data) {
        const data = (resp.data as any).data || resp.data;
        setRelations(data.relations || []);
      }
    } catch (err) {
      setRelations([]);
    } finally {
      setLoadingRelations(false);
    }
  };

  const loadCorpus = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await standardsApi.listStandards({
        q: searchTerm || undefined,
        category: categoryFilter || undefined,
        maturity: maturityFilter || undefined,
        include_archived: showArchived,
        page: currentPage,
        limit: pageSize,
      });
      if (resp.success && resp.data) {
        const data = (resp.data as any).data || resp.data;
        setStandards(data.Records || []);
        setTotalCount(data.pagination?.total || 0);
        setTotalPages(data.pagination?.total_pages || 0);
      } else {
        setError(resp.error || 'Failed to load standards');
      }
    } catch (err) {
      setError('Failed to load standards');
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, categoryFilter, maturityFilter, showArchived, currentPage]);

  useEffect(() => { loadCorpus(); }, [loadCorpus]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, maturityFilter, showArchived]);

  const openCreate = () => {
    setEditingStandard(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (s: CorpusStandard) => {
    setEditingStandard(s);
    setFormData({
      element: s.element || '',
      title: s.title || '',
      rule: s.rule || '',
      category: s.category || '',
      domain: s.domain || 'code',
      priority: s.priority || 50,
      content_type: s.content_type || 'standard',
      maturity: s.maturity || 'provisional',
      description: s.description || '',
      rationale: s.rationale || '',
      consequences: s.consequences || '',
      exceptions: s.exceptions || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      if (editingStandard) {
        const updates: StandardUpdateInput = {};
        const keys = Object.keys(formData) as (keyof StandardCreateInput)[];
        for (const key of keys) {
          const oldVal = (editingStandard as any)[key];
          const newVal = formData[key];
          if (newVal !== oldVal && newVal !== '') {
            (updates as any)[key] = newVal;
          }
        }
        if (Object.keys(updates).length === 0) {
          setShowModal(false);
          return;
        }
        const resp = await standardsApi.updateStandard(editingStandard.pattern_id, updates);
        if (!resp.success) { setError(resp.error || 'Update failed'); return; }
        setSuccessMsg(`Updated: ${editingStandard.element}`);
      } else {
        if (!formData.element || !formData.title || !formData.rule || !formData.category) {
          setError('element, title, rule, and category are required');
          return;
        }
        const resp = await standardsApi.createStandard(formData);
        if (!resp.success) { setError(resp.error || 'Create failed'); return; }
        setSuccessMsg(`Created: ${formData.element}`);
      }
      setShowModal(false);
      loadCorpus();
    } catch (err) {
      setError('Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      const resp = await standardsApi.retireStandard(archiveTarget.pattern_id, archiveReason || 'Retired via Corpus Management');
      if (!resp.success) { setError(resp.error || 'Archive failed'); return; }
      setSuccessMsg(`Archived: ${archiveTarget.element}`);
      setArchiveTarget(null);
      setArchiveReason('');
      loadCorpus();
    } catch (err) {
      setError('Archive failed');
    }
  };

  const handleExport = async () => {
    try {
      await standardsApi.downloadCorpus(undefined, !showArchived);
      setSuccessMsg('Corpus downloaded');
    } catch (err) {
      setError('Export failed');
    }
  };

  const uniqueCategories = [...new Set(standards.map(s => s.category).filter(Boolean))].sort();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Corpus Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            {totalCount} standard{totalCount !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button color="light" size="sm" onClick={loadCorpus}>
            <HiRefresh className="mr-1 h-4 w-4" /> Refresh
          </Button>
          <Button color="light" size="sm" onClick={handleExport}>
            <HiDownload className="mr-1 h-4 w-4" /> Export JSON
          </Button>
          <Button size="sm" onClick={openCreate}>
            <HiPlus className="mr-1 h-4 w-4" /> New Standard
          </Button>
        </div>
      </div>

      {error && (
        <Card className="bg-red-50 border-red-200">
          <p className="text-red-700 text-sm">{error}</p>
        </Card>
      )}
      {successMsg && (
        <Card className="bg-green-50 border-green-200">
          <p className="text-green-700 text-sm">{successMsg}</p>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px]">
            <TextInput
              icon={HiSearch}
              placeholder="Search element, title, rule..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              sizing="sm"
            />
          </div>
          <Select sizing="sm" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
            <option value="">All categories</option>
            {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
          <Select sizing="sm" value={maturityFilter} onChange={e => setMaturityFilter(e.target.value)}>
            <option value="">All maturity</option>
            {MATURITY_LEVELS.map(m => <option key={m} value={m}>{m}</option>)}
          </Select>
          <div className="flex items-center gap-2">
            <ToggleSwitch checked={showArchived} onChange={setShowArchived} label="Show archived" />
          </div>
        </div>
      </Card>

      {/* Standards Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell>Element</Table.HeadCell>
              <Table.HeadCell>Title</Table.HeadCell>
              <Table.HeadCell>Category</Table.HeadCell>
              <Table.HeadCell>Rule</Table.HeadCell>
              <Table.HeadCell>Maturity</Table.HeadCell>
              <Table.HeadCell>Priority</Table.HeadCell>
              <Table.HeadCell>Type</Table.HeadCell>
              <Table.HeadCell>Actions</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {standards.map(s => (
                <Table.Row key={s.pattern_id} className={!s.active ? 'opacity-50' : ''}>
                  <Table.Cell className="font-mono text-xs max-w-[150px] truncate" title={s.pattern_id}>
                    {s.element}
                  </Table.Cell>
                  <Table.Cell className="max-w-[250px] truncate" title={s.title}>
                    {s.title}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color="indigo" size="xs">{s.category}</Badge>
                  </Table.Cell>
                  <Table.Cell className="max-w-[200px] truncate text-xs" title={s.rule}>
                    {s.rule}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color={maturityColor(s.maturity)} size="xs">{s.maturity}</Badge>
                  </Table.Cell>
                  <Table.Cell className="text-center">{s.priority}</Table.Cell>
                  <Table.Cell>
                    <Badge color="gray" size="xs">{s.content_type}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex gap-1">
                      <Button size="xs" color="light" onClick={() => loadRelations(s.pattern_id)} title="Relations">
                        <HiLink className="h-3 w-3" />
                      </Button>
                      <Button size="xs" color="light" onClick={() => openEdit(s)} title="Edit">
                        <HiPencil className="h-3 w-3" />
                      </Button>
                      {s.active && (
                        <Button size="xs" color="failure" onClick={() => setArchiveTarget(s)} title="Archive">
                          <HiArchive className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
              {standards.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={8} className="text-center text-gray-500 py-8">
                    No standards found
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center mt-4">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </div>
        )}
      </Card>

      {/* Relations Panel */}
      {relationsStdId && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">
              Linked Entities: {standards.find(s => s.pattern_id === relationsStdId)?.element || relationsStdId}
            </h3>
            <Button size="xs" color="light" onClick={() => setRelationsStdId(null)}>Close</Button>
          </div>
          {loadingRelations ? (
            <Spinner size="sm" />
          ) : relations.length === 0 ? (
            <p className="text-sm text-gray-500 mb-3">No linked entities yet.</p>
          ) : (
            <div className="space-y-2 mb-4">
              {relations.map(r => (
                <div key={r.relation_id} className="flex items-center gap-2 text-sm border rounded p-2">
                  <Badge color="blue" size="xs">{r.relation_type}</Badge>
                  <span className="text-gray-400">{r.direction === 'outgoing' ? '→' : '←'}</span>
                  <Badge color={
                    r.target_type === 'entity' ? 'purple' :
                    r.target_type === 'reference' ? 'info' :
                    r.target_type === 'document' ? 'gray' : 'warning'
                  } size="xs">{r.target_type}</Badge>
                  <span className="font-medium">{r.target?.name || r.target_id}</span>
                  {r.target?.entity_type && <span className="text-xs text-gray-400">({r.target.entity_type})</span>}
                  {r.target?.document_type && <Badge color="indigo" size="xs">{r.target.document_type}</Badge>}
                  {r.target?.category && <Badge color="indigo" size="xs">{r.target.category}</Badge>}
                </div>
              ))}
            </div>
          )}
          <div className="border-t pt-3">
            <p className="text-sm font-medium mb-2">Add Link</p>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <TextInput
                  sizing="sm"
                  placeholder="Target object ID (document_id or pattern_id)"
                  value={linkTargetId}
                  onChange={e => setLinkTargetId(e.target.value)}
                />
              </div>
              <Select sizing="sm" value={linkTargetType} onChange={e => setLinkTargetType(e.target.value)}>
                <option value="entity">entity</option>
                <option value="reference">reference</option>
                <option value="document">document</option>
                <option value="invariant">invariant</option>
                <option value="competitive_intel">competitive_intel</option>
                <option value="collection">collection</option>
              </Select>
              <Select sizing="sm" value={linkRelationType} onChange={e => setLinkRelationType(e.target.value)}>
                <option value="related_to">related_to</option>
                <option value="informs">informs</option>
                <option value="extracted_from">extracted_from</option>
                <option value="supersedes">supersedes</option>
                <option value="part_of">part_of</option>
                <option value="recommended_by">recommended_by</option>
                <option value="contradicts">contradicts</option>
              </Select>
              <Button size="sm" onClick={handleAddLink} disabled={isLinking || !linkTargetId}>
                {isLinking ? <Spinner size="sm" /> : 'Link'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)} size="4xl">
        <Modal.Header>{editingStandard ? `Edit: ${editingStandard.element}` : 'New Standard'}</Modal.Header>
        <Modal.Body>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Element *</label>
              <TextInput
                value={formData.element}
                onChange={e => setFormData(prev => ({ ...prev, element: e.target.value }))}
                placeholder="no-mocks-in-tests"
                sizing="sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category *</label>
              <Select
                sizing="sm"
                value={formData.category}
                onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
              >
                <option value="">Select...</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Title *</label>
              <TextInput
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="No mock data in integration tests"
                sizing="sm"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Rule *</label>
              <Textarea
                value={formData.rule}
                onChange={e => setFormData(prev => ({ ...prev, rule: e.target.value }))}
                placeholder="NEVER use mock data in integration tests — mock data hides real API failures"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Domain</label>
              <Select
                sizing="sm"
                value={formData.domain}
                onChange={e => setFormData(prev => ({ ...prev, domain: e.target.value }))}
              >
                <option value="code">code</option>
                <option value="business">business</option>
                <option value="operations">operations</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Content Type</label>
              <Select
                sizing="sm"
                value={formData.content_type}
                onChange={e => setFormData(prev => ({ ...prev, content_type: e.target.value }))}
              >
                {CONTENT_TYPES.map(ct => <option key={ct} value={ct}>{ct}</option>)}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Maturity</label>
              <Select
                sizing="sm"
                value={formData.maturity}
                onChange={e => setFormData(prev => ({ ...prev, maturity: e.target.value }))}
              >
                {MATURITY_LEVELS.map(m => <option key={m} value={m}>{m}</option>)}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Priority (1-100)</label>
              <TextInput
                type="number"
                min={1}
                max={100}
                value={formData.priority}
                onChange={e => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 50 }))}
                sizing="sm"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                value={formData.description || ''}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Rationale</label>
              <Textarea
                value={formData.rationale || ''}
                onChange={e => setFormData(prev => ({ ...prev, rationale: e.target.value }))}
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Consequences</label>
              <Textarea
                value={formData.consequences || ''}
                onChange={e => setFormData(prev => ({ ...prev, consequences: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="mr-2" /> : null}
            {editingStandard ? 'Update' : 'Create'}
          </Button>
          <Button color="gray" onClick={() => setShowModal(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal show={!!archiveTarget} onClose={() => setArchiveTarget(null)} size="md">
        <Modal.Header>Archive Standard</Modal.Header>
        <Modal.Body>
          <p className="mb-4">
            Archive <strong>{archiveTarget?.element}</strong>? It will be set to inactive and excluded from injection.
          </p>
          <TextInput
            placeholder="Reason (optional)"
            value={archiveReason}
            onChange={e => setArchiveReason(e.target.value)}
            sizing="sm"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={handleArchive}>Archive</Button>
          <Button color="gray" onClick={() => { setArchiveTarget(null); setArchiveReason(''); }}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CorpusManagement;
